// LiveChat.jsx — spinosoftbitsAI, powered by Gemini (via /api/chat).
// Keeps the existing widget shell + livechat.css. Every conversation is saved
// server-side (by sessionId) so any chat can be pulled later from the admin API.
// When the agent gathers a name + email + need, it hands off to /api/leads/submit.

import React, { useState, useEffect, useRef } from 'react';
import './livechat.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const WELCOME =
  "Hey! 👋 I'm spinosoftbitsAI, Spinoza's assistant at SpinoSoftBits. Tell me what you're trying to build and I'll point you the right way.";

// Stable id for this chat session (used to group + retrieve the transcript).
const makeSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `chat_${crypto.randomUUID()}`;
  } catch (e) {}
  return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(makeSessionId());

  // Seed the welcome message the first time the chat is opened.
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ text: WELCOME, sender: 'bot' }]);
    }
  }, [isOpen, messages.length]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Hand the captured lead to the existing public endpoint (admin + welcome emails fire there).
  const submitLead = async (lead) => {
    try {
      await fetch(`${API_BASE}/api/leads/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.name,
          email: lead.email,
          service: lead.service || 'general',
          budget: lead.budget || '',
          message: lead.message || '',
          timeline: lead.timeline || 'ASAP',
          company: lead.company || '',
          phone: lead.phone || '',
          source: 'Live Chat AI',
          page: typeof window !== 'undefined' ? window.location.pathname : '',
        }),
      });
    } catch (err) {
      console.error('Lead submit failed:', err);
    }
  };

  const sendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = input.trim();
    if (!text || isLoading) return;

    const next = [...messages, { text, sender: 'user' }];
    setMessages(next);
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: next,
          sessionId: sessionId.current,
          page: typeof window !== 'undefined' ? window.location.pathname : '',
        }),
      });
      const data = await res.json();

      const reply =
        data?.reply || "Sorry, I didn't catch that — could you say it another way?";
      setMessages((prev) => [...prev, { text: reply, sender: 'bot' }]);

      if (data?.leadReady && data?.lead && !leadSubmitted) {
        setLeadSubmitted(true);
        submitLead(data.lead);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) => [
        ...prev,
        {
          text: "I'm having trouble connecting right now. Please use the Contact form and Spinoza will get back to you.",
          sender: 'bot',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        className={`chat-button ${isOpen ? 'chat-open' : 'chat-vibrate'}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open chat"
      >
        {isOpen ? (
          <i className="uil uil-times"></i>
        ) : (
          <>
            <i className="uil uil-comment-dots"></i>
            <span className="chat-button-badge">1</span>
            <span className="chat-button-pulse"></span>
            <span className="chat-button-pulse-2"></span>
          </>
        )}
      </button>

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-header-info">
                <h3>spinosoftbitsAI</h3>
                <span className="chat-status">
                  <span className="status-dot"></span>
                  Online · usually replies instantly
                </span>
              </div>
            </div>
            <button
              className="chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <i className="uil uil-times"></i>
            </button>
          </div>

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.sender} ${msg.type || ''}`}>
                <div>{msg.text}</div>
              </div>
            ))}
            {isLoading && (
              <div className="message bot typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="chat-form">
            <div className="chat-input-group">
              <input
                type="text"
                placeholder="Type your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="chat-input"
                maxLength={500}
              />
              <button
                type="submit"
                className="chat-send"
                disabled={!input.trim() || isLoading}
              >
                <i className="uil uil-message"></i>
              </button>
            </div>
          </form>

          {/* Honest, low-key monitoring notice */}
          <div
            className="chat-disclaimer"
            style={{
              fontSize: '11px',
              lineHeight: 1.4,
              textAlign: 'center',
              opacity: 0.6,
              padding: '6px 12px 10px',
            }}
          >
            <i className="uil uil-lock-alt" style={{ fontSize: '12px', marginRight: '4px' }}></i>
            Chats with spinosoftbitsAI may be monitored &amp; recorded to improve service.
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChat;